import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { CollectedAt, Pill, label } from './components/primitives'
import {
  createPlan, listCollectors, listPlans, listSources, listSubjects, runPlanNow, setPlanEnabled,
  type Collector, type Plan, type Source, type Subject,
} from './api'

/**
 * Collection plans — what is collected, on whom, how often, and why.
 *
 * ## The purpose is a column, not a detail
 *
 * A plan pointed at a named person is standing surveillance, and the sentence somebody wrote
 * in `purpose` is what the audit row carries when they are asked to justify it. Putting it in
 * a drawer would make the most consequential field the least visible one.
 *
 * ## Three failures disables a plan, and the count is shown before it gets there
 */
export function PlansPage() {
  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [sources, setSources] = useState<Source[]>([])
  const [collectors, setCollectors] = useState<Collector[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])

  const load = useCallback(async () => {
    try {
      setError(null)
      const [p, src, col] = await Promise.all([listPlans(), listSources(), listCollectors()])
      setPlans(p)
      setSources(src)
      setCollectors(col)
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listSubjects({ size: '200' }).then(setSubjects).catch(() => setSubjects([]))
  }, [])

  async function toggle(plan: Plan) {
    setBusy(plan.id)
    try {
      await setPlanEnabled(plan.id, !plan.enabled)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  async function runNow(plan: Plan) {
    setBusy(plan.id)
    try {
      await runPlanNow(plan.id)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  if (error && !plans) return <ErrorStrip message={error} />
  if (!plans) return <LoadingBlock label="Loading plans" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Collection plans"
        description="Enabling one is a separate, audited act — it is what begins standing
                     collection on a subject."
      />
      {error && <ErrorStrip message={error} />}

      <div className="flex justify-end">
        <button type="button" onClick={() => setAdding((a) => !a)}
                className="rounded border border-border px-2 py-1 text-[11px]
                           hover:bg-muted/40">
          {adding ? 'Cancel' : 'New plan'}
        </button>
      </div>

      {adding && (
        <AddPlanForm sources={sources} collectors={collectors} subjects={subjects}
                     onDone={() => { setAdding(false); void load() }} />
      )}

      <Panel>
        <ErpTable head={<>
          <Th>Plan</Th>
          <Th>Driver</Th>
          <Th className="axis-hide-sm">Cadence</Th>
          <Th>Purpose</Th>
          <Th className="text-right axis-hide-sm">Failures</Th>
          <Th>Next run</Th>
          <Th>State</Th>
        </>}>
          {plans.length === 0 && (
            <EmptyRow colSpan={7}
                      message="No plans yet. 'New plan' above says what to collect, on whom and
                               how often — a plan is created STOPPED and starting it is a
                               separate, audited act." />
          )}
          {plans.map((plan) => (
            <tr key={plan.id} className="hover:bg-muted/40">
              <Td><span className="font-medium">{plan.name}</span></Td>
              <Td><span className="font-mono text-[11px]">{plan.collectorCode}</span></Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px]">{label(plan.cadenceCode)}</span>
              </Td>
              {/* The most consequential field, in the most visible place. */}
              <Td><span className="text-[11px] text-muted-foreground">{plan.purpose}</span></Td>
              <Td className="text-right tabular-nums axis-hide-sm">
                <span className={plan.consecutiveFailures >= 2 ? 'text-rose-600' : ''}>
                  {plan.consecutiveFailures}/3
                </span>
              </Td>
              <Td><CollectedAt at={plan.nextRunAt} /></Td>
              <Td>
                <div className="flex items-center gap-1">
                  <Pill className={plan.enabled
                    ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                    : 'border-border text-muted-foreground'}>
                    {plan.enabled ? 'Collecting' : 'Stopped'}
                  </Pill>
                  <button type="button" disabled={busy === plan.id}
                          onClick={() => void toggle(plan)}
                          className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                    {plan.enabled ? 'Stop' : 'Start'}
                  </button>
                  <button type="button" disabled={busy === plan.id}
                          onClick={() => void runNow(plan)}
                          className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                    Run now
                  </button>
                </div>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}

function Field({ label: text, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1 text-[11px]">
      <span className="text-muted-foreground">{text}</span>
      {children}
    </label>
  )
}

const INPUT = 'h-7 rounded border border-input bg-background px-2 text-[12px]'

/**
 * Create a plan.
 *
 * <p>The gap this closes: `createPlan` has existed on the client since the feature shipped and
 * nothing called it, so plans could only be made through the API. The empty state told
 * operators to "create one from a subject's file", where there was no such button either.</p>
 *
 * The parameter fields are GENERATED from the selected source's driver, the same bargain
 * SourcesPage makes: a new collector gets a working form without a frontend release, and a
 * mistyped key becomes a validation error at save rather than a plan that silently collects
 * nothing.
 */
function AddPlanForm({ sources, collectors, subjects, onDone }: {
  sources: Source[]; collectors: Collector[]; subjects: Subject[]; onDone: () => void
}) {
  const [name, setName] = useState('')
  const [sourceId, setSourceId] = useState('')
  const [subjectId, setSubjectId] = useState('')
  const [cadenceCode, setCadenceCode] = useState('SIX_HOURLY')
  const [purpose, setPurpose] = useState('')
  const [maxItemsPerRun, setMaxItemsPerRun] = useState('100')
  const [params, setParams] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Only sources that can actually be fetched from. A MANUAL desk source has no driver, so a
  // plan pointed at one would be a schedule that can never run.
  const fetchable = sources.filter((s) => s.driverCode)
  const source = fetchable.find((s) => s.id === sourceId)
  const driver = collectors.find((c) => c.code === source?.driverCode)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await createPlan({
        name,
        sourceId,
        subjectId: subjectId || null,
        params: Object.fromEntries(
          Object.entries(params).filter(([, v]) => v != null && String(v).trim() !== '')),
        cadenceCode,
        // The driver declares the basis it collects under; the plan may not invent a wider one.
        legalBasisCode: driver?.legalBasisCode ?? 'OPEN_SOURCE',
        purpose,
        maxItemsPerRun: Number(maxItemsPerRun) || 100,
        lookbackDays: null,
      })
      onDone()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel>
      <form onSubmit={submit} className="grid gap-2 p-3 sm:grid-cols-2">
        {error && <div className="sm:col-span-2"><ErrorStrip message={error} /></div>}

        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} required
                 placeholder="Social — opposition principals" className={INPUT} />
        </Field>

        <Field label="Source">
          <select value={sourceId} required className={INPUT}
                  onChange={(e) => { setSourceId(e.target.value); setParams({}) }}>
            <option value="">Choose a source…</option>
            {fetchable.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.driverCode})</option>
            ))}
          </select>
        </Field>

        <Field label="Subject (optional — leave blank to collect on every subject)">
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                  className={INPUT}>
            <option value="">Every subject</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>{s.displayName}</option>
            ))}
          </select>
        </Field>

        <Field label="How often">
          <select value={cadenceCode} onChange={(e) => setCadenceCode(e.target.value)}
                  className={INPUT}>
            {['MANUAL', 'HOURLY', 'SIX_HOURLY', 'DAILY', 'WEEKLY'].map((c) => (
              <option key={c} value={c}>{label(c)}</option>
            ))}
          </select>
        </Field>

        <Field label="Most items one run may collect">
          <input value={maxItemsPerRun} onChange={(e) => setMaxItemsPerRun(e.target.value)}
                 type="number" min={1} max={5000} className={INPUT} />
        </Field>

        {/*
          The sentence the audit row carries every time this plan runs. Ten characters minimum
          is the server's rule, and it exists because "monitoring" is not a justification a
          person can be asked to stand behind later.
        */}
        <Field label="Why this platform is collecting on this subject (required)">
          <input value={purpose} onChange={(e) => setPurpose(e.target.value)} required
                 minLength={10}
                 placeholder="Track public claims about our principals ahead of the 2027 cycle"
                 className={INPUT} />
        </Field>

        {driver && driver.configKeys.length > 0 && (
          <div className="grid gap-2 sm:col-span-2 sm:grid-cols-2">
            <p className="text-[11px] text-muted-foreground sm:col-span-2">
              {driver.label} settings — a blank field falls back to the source's own.
            </p>
            {driver.configKeys.map((key) => (
              <Field key={key.key}
                     label={key.label + (key.required ? '' : ' (optional)')}>
                <input value={params[key.key] ?? ''}
                       onChange={(e) =>
                         setParams((p) => ({ ...p, [key.key]: e.target.value }))}
                       placeholder={key.example ?? ''} className={INPUT} />
              </Field>
            ))}
          </div>
        )}

        <div className="sm:col-span-2">
          <button type="submit" disabled={busy || !sourceId}
                  className="rounded border border-border px-2 py-1 text-[11px]
                             hover:bg-muted/40 disabled:opacity-50">
            {busy ? 'Creating…' : 'Create plan (stopped)'}
          </button>
        </div>
      </form>
    </Panel>
  )
}
